// import APIs
import { queries, requests } from 'RestAPIs/rapini';
// import components
import { Footer } from './components';
import Header from './components/Header/Header';
import TravelDetailDialog from './components/TravelDetailDialog';
import WarningDialogAdvanced from './components/WarningDialogAdvanced';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import WarningDialog from 'components/shared/WarningDialog/WarningDialog';
import { Box } from '@mui/material';

// import external libraries
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { RecoilRoot } from 'recoil';

// import hooks
import { useNavParams, useSelector } from 'hooks';
import useColumnDefinitions from './useColumnDefinitions';
import { ClaimsAction } from './types';

// import stores
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';

// import types
import { ExpenseClaimResponse } from 'generated/index';
import { PlanGroup, PlanUnitMix } from 'components/types';
import { TravelExpenseClaimsProps } from './TravelExpenseClaims.types';

// import utils
import { getCurrentlyLoggedInEmployeeId } from 'utils/misc';
import { GridRowId } from '@mui/x-data-grid/models/gridRows';

const TravelExpenseClaims = ({
  page = 'START',
  selectedEmployeeId,
  disableActions = false
}: TravelExpenseClaimsProps) => {
  const { hasTravelMovementsFeature } = useSelector(({ grants }) => grants);

  if (hasTravelMovementsFeature) {
    return null;
  }

  const columns = useColumnDefinitions();
  const { params } = useNavParams();
  const { t } = useTranslation('common');
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnitMix[]>([]);
  const { selectedDate, setSelectedDate } = useDateNavigationHoursStore(
    (state) => state
  );
  const [selectedRows, setSelectedRows] = useState<ExpenseClaimResponse[]>([]);
  const [dayOfMonth, setDayOfMonth] = useState<Date | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<
    [boolean, ClaimsAction | null]
  >([false, null]);
  const [showRejectDialog, setShowRejectDialog] = useState<
    [boolean, ClaimsAction | null]
  >([false, null]);
  const [activeClaim, setActiveClaim] = useState<ExpenseClaimResponse | null>(
    null
  );

  const isOverview = !page || page === 'START';
  const isEmployeeCard = page === 'EMPLOYEE_CARD';
  const isEmployeePortal = page === 'EMPLOYEE_PORTAL';
  const isEmployee = isEmployeeCard || isEmployeePortal;

  const loggedInUserEmployeeId = +getCurrentlyLoggedInEmployeeId();
  const startDate = dayjs(selectedDate).startOf('month').format('YYYY-MM-DD');
  const endDate = dayjs(selectedDate)
    .add(1, 'month')
    .startOf('month')
    .format('YYYY-MM-DD');

  const queryClient = useQueryClient();

  // Expense claims by employee
  const getExpenseClaimsByEmployee = () =>
    queries.useFindExpenseClaimsByEmployee(
      startDate,
      endDate,
      isEmployeePortal
        ? loggedInUserEmployeeId
        : (selectedEmployeeId as number),
      {
        enabled: isEmployee && !!(loggedInUserEmployeeId || selectedEmployeeId)
      }
    );

  const planGroup = selectedPlanUnits
    .filter((planunit): planunit is PlanGroup => 'costCenters' in planunit)
    .flatMap((planunit) => planunit.costCenters)
    .map((costCenter) => costCenter.id)
    .join(',');

  const preparePlanUnitIdsForApi = !!planGroup
    ? planGroup
    : selectedPlanUnits.map((planunit) => planunit.id).join(',');

  // Expense claims by organizational unit
  const getExpenseClaimsByOrganizationalUnits = () =>
    queries.useFindExpenseClaimsByOrganizationalUnits(
      startDate,
      endDate,
      //@ts-expect-error TODO: Rapini should accept array of ids
      preparePlanUnitIdsForApi,
      {
        enabled: !isEmployee && selectedPlanUnits.length > 0
      }
    );

  const {
    data = [],
    refetch: refetchExpenseClaims,
    isLoading
  } = isEmployee
    ? getExpenseClaimsByEmployee()
    : getExpenseClaimsByOrganizationalUnits();

  const rows = data.map((ecr: ExpenseClaimResponse) => ({
    ...ecr,
    transportationTypeName: ecr.transportationType?.name,
    disableSelection: !ecr.canBeApproved
  }));

  const totalKm =
    rows.reduce(
      (acc, row) =>
        acc +
        row.routes.reduce((acc, route) => acc + route.distanceInMeters, 0),
      0
    ) / 1000;

  const approveClaims = async () => {
    const { approveExpenseClaims } = requests;

    try {
      await approveExpenseClaims({
        expenseClaimIds: selectedRows
          .filter(({ canBeApproved }) => canBeApproved)
          .map(({ id }) => id)
      });

      queryClient.invalidateQueries(
        isEmployee
          ? 'findExpenseClaimsByEmployee'
          : 'findExpenseClaimsByOrganizationalUnits'
      );

      showSuccessSnackbar();
    } catch (err) {
      showErrorSnackbar((err as AxiosError)?.message);
    } finally {
      setShowWarningDialog([false, null]);
      setSelectedRows([]);
    }
  };

  const rejectClaims = async (formData) => {
    const { rejectExpenseClaims } = requests;

    try {
      await rejectExpenseClaims({
        expenseClaimIds: selectedRows.map(({ id }) => id),
        statusNote: formData.remark
      });

      queryClient.invalidateQueries(
        isEmployee
          ? 'findExpenseClaimsByEmployee'
          : 'findExpenseClaimsByOrganizationalUnits'
      );

      showSuccessSnackbar();
    } catch (err) {
      showErrorSnackbar((err as AxiosError)?.message);
    } finally {
      setShowRejectDialog([false, null]);
      // Clear selected rows only after dialog closes
      setSelectedRows([]);
    }
  };

  const handleAction = (action: ClaimsAction) => {
    switch (action) {
      case ClaimsAction.APPROVE:
        setShowWarningDialog([true, ClaimsAction.APPROVE]);
        break;
      case ClaimsAction.REJECT:
        setShowRejectDialog([true, ClaimsAction.REJECT]);
        break;
      default:
        break;
    }
  };

  const handleCloseDetailDialog = () => {
    setShowDetailDialog(false);
    setActiveClaim(null);
  };

  const handleActionClick = (
    id: string | number,
    row: ExpenseClaimResponse,
    action: ClaimsAction
  ) => {
    // Ensure the row is selected
    row &&
      !selectedRows.some((r) => r.id === row.id) &&
      setSelectedRows((prev) => [...prev, row]);

    // Set the active claim
    setActiveClaim(rows.find((r) => r.id === id)!);

    // Trigger the action (Approve or Reject)
    handleAction?.(action);
  };

  useEffect(() => {
    if (selectedPlanUnits.length > 0) refetchExpenseClaims();
  }, [selectedPlanUnits, selectedDate, page]);

  // Update the selected date when the URL changes
  useEffect(() => {
    if (params?.date && dayjs(params.date, 'DD-MM-YYYY').isValid()) {
      const newDate = dayjs(params.date, 'DD-MM-YYYY').toDate();

      if (newDate.getTime() !== dayOfMonth?.getTime()) {
        setDayOfMonth(newDate);
        setSelectedDate(newDate);
      }
    }
  }, [params?.date, dayOfMonth]);

  return (
    <RecoilRoot>
      <Header
        onAction={handleAction}
        onSelectPlanUnit={setSelectedPlanUnits}
        selectedDate={selectedDate}
        onSelectedDate={setSelectedDate}
        selection={selectedRows}
        showActions={isOverview}
        showPlanUnitSelector={isOverview}
      />
      <Box
        sx={{
          mt: (theme) => theme.spacing(1),
          height: '100%',
          overflow: 'auto'
        }}
      >
        <TableCRUDLayout<ExpenseClaimResponse>
          rows={rows}
          columns={columns}
          editable={false}
          pageSize={50}
          pageSizeOptions={[5]}
          checkboxSelection={rows.some(
            ({ disableSelection }) => !disableSelection
          )}
          footer={
            isEmployeeCard ? (
              <Footer>
                <Box mr={1}>
                  {t('travelExpenseClaims.totalKm')}: {totalKm}
                </Box>
              </Footer>
            ) : null
          }
          customSelectionModel={{
            type: 'include',
            ids: new Set<GridRowId>(selectedRows.map(({ id }) => id))
          }}
          onSelectionChange={(_, rows = []) => {
            setSelectedRows(rows);
          }}
          onViewClick={(
            id: string | number,
            _row,
            event?: MouseEvent<HTMLButtonElement>
          ) => {
            event?.preventDefault();
            event?.stopPropagation();
            setActiveClaim(rows.find((r) => r.id === id)!);
            setShowDetailDialog(true);
          }}
          customActions={(id, row) => [
            {
              tooltipTitle: t('travelExpenseClaims.actions.APPROVE'),
              icon: 'check',
              onClick: (id, _row) =>
                row && handleActionClick(id, row, ClaimsAction.APPROVE),
              hidden:
                disableActions ||
                !row?.canBeApproved ||
                row?.status !== 'SUBMITTED'
            },
            {
              tooltipTitle: t('travelExpenseClaims.actions.REJECT'),
              icon: 'xmark',
              onClick: (id, _row) =>
                row && handleActionClick(id, row, ClaimsAction.REJECT),
              hidden:
                disableActions ||
                !row?.canBeApproved ||
                row?.status !== 'SUBMITTED'
            }
          ]}
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'asc' }]
            }
          }}
          sx={{
            mt: 1,
            // Hide the checkbox if the row/input is disabled
            '.MuiCheckbox-root input[type="checkbox"]:disabled + *': {
              display: 'none'
            }
          }}
        />
      </Box>
      <WarningDialog
        title={t('travelExpenseClaims.warning', {
          action: t(`travelExpenseClaims.actions.${showWarningDialog[1]}`)
        })}
        open={showWarningDialog[0]}
        onSubmit={() => approveClaims()}
        onCancel={() => {
          setShowWarningDialog([false, null]);
        }}
      />
      <WarningDialogAdvanced
        title={t('travelExpenseClaims.warning', {
          action: t(`travelExpenseClaims.actions.${showRejectDialog[1]}`)
        })}
        open={showRejectDialog[0]}
        selectedRows={selectedRows}
        onSubmit={(formData) => rejectClaims(formData)}
        onCancel={() => {
          setShowRejectDialog([false, null]);
        }}
      />

      <TravelDetailDialog
        claim={activeClaim}
        isOpen={showDetailDialog}
        onClose={handleCloseDetailDialog}
      />
      <LoadingOverlay loading={isLoading} />
    </RecoilRoot>
  );
};

export default TravelExpenseClaims;
